import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Phone, MessageSquare, Building2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Eyebrow, Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null); // null = checking, false = login, object = authenticated
  const [leads, setLeads] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadLeads = async () => {
    const { data } = await api.get("/admin/leads");
    setLeads(data);
  };

  const checkSession = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadLeads().catch(() => setLeads([]));
    } catch (e) {
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        setUser(data);
        loadLeads().catch(() => setLeads([]));
      } catch {
        setUser(false);
      }
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      loadLeads().catch(() => setLeads([]));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Accesso non riuscito.");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setUser(false);
    setLeads(null);
  };

  /* ---------- Login ---------- */
  if (user === false) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-ink px-6 py-24" data-testid="admin-login-view">
        <Seo title="Area riservata | IDEAL REAL ESTATE" description="Area riservata IDEAL REAL ESTATE." path="/admin" noindex />
        <div className="w-full max-w-md">
          <div className="flex justify-center">
            <Logo theme="sand" />
          </div>
          <h1 className="mt-10 text-center font-serif text-4xl text-sand">Area riservata</h1>
          <p className="mt-3 text-center text-sm font-light text-sand/50">
            Accedi con le credenziali amministratore per leggere le richieste ricevute dal sito.
          </p>
          <form onSubmit={login} className="mt-10 space-y-7" data-testid="admin-login-form">
            <div>
              <label className="field-label !text-sand/50" htmlFor="admin-email">Email</label>
              <input
                id="admin-email"
                data-testid="admin-login-email"
                className="field-input !border-sand/25 !text-sand placeholder:!text-sand/30"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@idealrealestate.it"
              />
            </div>
            <div>
              <label className="field-label !text-sand/50" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                data-testid="admin-login-password"
                className="field-input !border-sand/25 !text-sand placeholder:!text-sand/30"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••••"
              />
            </div>
            {error && (
              <p className="border-l-2 border-red-400 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="w-full rounded-full bg-sand py-4 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors duration-300 hover:bg-champagne hover:text-sand disabled:opacity-50"
            >
              {busy ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>
          <button
            onClick={() => navigate("/")}
            className="mx-auto mt-8 block text-[11px] uppercase tracking-wide2 text-sand/40 transition-colors hover:text-sand"
          >
            ← Torna al sito
          </button>
        </div>
      </div>
    );
  }

  /* ---------- Checking ---------- */
  if (!user) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-mut" />
      </div>
    );
  }

  /* ---------- Dashboard ---------- */
  return (
    <div className="min-h-[100svh] bg-sand pt-28 lg:pt-32" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow>Area riservata</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl font-medium lg:text-5xl">Richieste ricevute</h1>
            <p className="mt-3 text-sm font-light text-mut">
              Accesso effettuato come <span className="font-mono">{user.email}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { setLeads(null); loadLeads().catch(() => setLeads([])); }}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-[11px] font-medium uppercase tracking-wide2 text-ink transition-colors hover:border-champagne hover:text-champagne"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-[11px] font-medium uppercase tracking-wide2 text-sand transition-colors hover:bg-champagne"
            >
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>

        {leads === null ? (
          <div className="mt-14 space-y-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse bg-sand3" />)}
          </div>
        ) : (
          <>
            <div className="mt-12 grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-4 lg:grid-cols-6">
              <div className="bg-sand2 p-5">
                <p className="text-[10px] uppercase tracking-wide2 text-mut">Totale</p>
                <p className="mt-2 font-mono text-2xl font-medium" data-testid="admin-leads-total">{leads.total}</p>
              </div>
              {["Vendita", "Acquisto", "Locazione", "Consulenza", "Visita / Informazioni"].map((r) => (
                <div key={r} className="bg-sand2 p-5">
                  <p className="text-[10px] uppercase tracking-wide2 text-mut">{r === "Visita / Informazioni" ? "Visite/Info" : r}</p>
                  <p className="mt-2 font-mono text-2xl font-medium">{leads.stats[r] || 0}</p>
                </div>
              ))}
            </div>

            {leads.leads.length === 0 ? (
              <div className="mt-14 border border-dashed border-ink/20 p-14 text-center">
                <p className="font-serif text-2xl italic text-mut">Nessuna richiesta ricevuta, per ora.</p>
              </div>
            ) : (
              <div className="mt-10 overflow-x-auto border border-ink/10" data-testid="admin-leads-table">
                <table className="w-full min-w-[860px] border-collapse text-left">
                  <thead>
                    <tr className="bg-ink text-sand">
                      {["Data", "Nome", "Contatti", "Motivo", "Immobile", "Messaggio"].map((h) => (
                        <th key={h} className="px-5 py-4 text-[10px] font-medium uppercase tracking-wide2">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.leads.map((lead, i) => (
                      <tr key={i} data-testid="admin-lead-row" className="border-t border-ink/10 align-top transition-colors hover:bg-sand2">
                        <td className="whitespace-nowrap px-5 py-4 font-mono text-[12px] text-mut">{fmtDate(lead.created_at)}</td>
                        <td className="px-5 py-4 font-serif text-lg">{lead.name}</td>
                        <td className="px-5 py-4 text-[13px] font-light">
                          <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-champagne" /> {lead.email}</p>
                          <p className="mt-1 flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-champagne" /> {lead.phone}</p>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-block bg-champsoft px-3 py-1 font-mono text-[11px] text-ink">{lead.reason}</span>
                        </td>
                        <td className="px-5 py-4 text-[13px] font-light">
                          {lead.property_title ? (
                            <span className="flex items-start gap-2"><Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-champagne" /> {lead.property_title}</span>
                          ) : lead.context ? (
                            <span className="font-mono text-[12px] text-mut">{lead.context}</span>
                          ) : "—"}
                        </td>
                        <td className="max-w-xs px-5 py-4 text-[13px] font-light text-ink2">
                          {lead.message ? (
                            <span className="flex items-start gap-2"><MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-champagne" /> {lead.message}</span>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
